import "./DevStatus.css";

export default function DevStatus() {
  const projectInfo = {
    name: "Pandemic Relief Connect",
    version: "0.1.0",
    status: "Development Phase",
    lastUpdated: "January 2025",
    description: "A modern React-based platform connecting communities during crisis situations through donation coordination and relief efforts."
  };

  const techStack = {
    "Frontend": [
      { name: "React", version: "18.2.0", status: "✅" },
      { name: "React Router", version: "6.22.0", status: "✅" },
      { name: "CSS3 + Bootstrap", version: "4.x", status: "✅" }
    ],
    "Development": [
      { name: "Node.js", version: "18+", status: "✅" },
      { name: "React Scripts", version: "5.0.1", status: "✅" },
      { name: "Docker", version: "Latest", status: "✅" }
    ],
    "Testing": [
      { name: "Jest + Testing Library", version: "Latest", status: "🟡" },
      { name: "Unit Tests", version: "N/A", status: "📋" }
    ]
  };

  const features = {
    "✅ Completed": [
      "Single Page Application architecture",
      "Responsive design (mobile + desktop)",
      "Component-based React structure",
      "Docker development environment",
      "Home page with hero section",
      "Donation items showcase",
      "Navigation and routing"
    ],
    "🚧 In Progress": [
      "Donation flow forms",
      "User authentication UI",
      "Location-based services"
    ],
    "📋 Planned": [
      "Backend API integration",
      "Database connectivity",
      "Real-time tracking",
      "Payment gateway",
      "Admin dashboard",
      "Mobile app version"
    ]
  };

  const stats = [
    { label: "Components", value: "15+", icon: "⚛️" },
    { label: "Routes", value: "3", icon: "🛣️" },
    { label: "Dependencies", value: "8", icon: "📦" },
    { label: "Docker Ready", value: "✅", icon: "🐳" }
  ];

  const commands = [
    { name: "Start Dev", command: "make start", desc: "Launch development server" },
    { name: "Build Prod", command: "make prod", desc: "Create production build" },
    { name: "Run Tests", command: "make test", desc: "Execute test suite" },
    { name: "View Logs", command: "make logs", desc: "Monitor application logs" }
  ];

  return (
    <div className="dev-status-container">
      <div className="container">
        {/* Header */}
        <div className="dev-status-header">
          <div className="dev-status-logo">
            <span className="dev-logo-pandemic">Pandemic</span>
            <span className="dev-logo-relief">Relief</span>
            <span className="dev-logo-connect">Connect</span>
          </div>
          <h1>Development Status</h1>
        </div>

        {/* Project Info Card */}
        <section className="project-info-section">
          <div className="project-info-card">
            <div className="project-header">
              <h2>{projectInfo.name}</h2>
              <div className="project-badges">
                <span className="version-badge">v{projectInfo.version}</span>
                <span className="status-badge development">{projectInfo.status}</span>
              </div>
            </div>
            <p className="project-description">{projectInfo.description}</p>
            <div className="project-meta">
              <span>Last Updated: {projectInfo.lastUpdated}</span>
            </div>
          </div>
        </section>

        {/* Tech Stack */}
        <section className="tech-section">
          <h2>Technology Stack</h2>
          <div className="tech-grid">
            {Object.entries(techStack).map(([category, items]) => (
              <div key={category} className="tech-category-card">
                <h3>{category}</h3>
                <div className="tech-items">
                  {items.map((tech, index) => (
                    <div key={index} className="tech-item">
                      <span className="tech-name">{tech.name}</span>
                      <span className="tech-version">{tech.version}</span>
                      <span className="tech-status">{tech.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="features-section">
          <h2>Features & Progress</h2>
          <div className="features-grid">
            {Object.entries(features).map(([status, items]) => (
              <div key={status} className="feature-category">
                <h3>{status}</h3>
                <ul>
                  {items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="stats-section">
          <h2>Project Statistics</h2>
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Development Commands */}
        <section className="commands-section">
          <h2>Quick Commands</h2>
          <div className="commands-grid">
            {commands.map((cmd, index) => (
              <div key={index} className="command-card">
                <div className="command-header">
                  <h4>{cmd.name}</h4>
                  <code>{cmd.command}</code>
                </div>
                <p>{cmd.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Roadmap */}
        <section className="roadmap-section">
          <h2>Development Roadmap</h2>
          <div className="roadmap-timeline">
            <div className="roadmap-item">
              <h3>🎯 Phase 1: Core Backend (Q1 2025)</h3>
              <ul>
                <li>API development (Node.js/Express)</li>
                <li>Database integration (PostgreSQL)</li>
                <li>User authentication system</li>
                <li>Complete donation flow forms</li>
              </ul>
            </div>
            <div className="roadmap-item">
              <h3>🚀 Phase 2: Advanced Features (Q2-Q3 2025)</h3>
              <ul>
                <li>Real-time updates & notifications</li>
                <li>Geolocation & mapping integration</li>
                <li>Payment gateway integration</li>
                <li>Admin dashboard & analytics</li>
              </ul>
            </div>
            <div className="roadmap-item">
              <h3>📱 Phase 3: Platform Expansion (Q4 2025)</h3>
              <ul>
                <li>React Native mobile app</li>
                <li>Advanced reporting tools</li>
                <li>Route optimization algorithms</li>
                <li>Multi-language support</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}